// miniapp/src/components/chat/ReplyContextBar.tsx
//
// Phase 33: Telegram-style quoted-reply preview rendered above the
// composer input when a reply target is active. Mirrors Telegram's
// native UX — left accent stripe in iOS blue, target message
// identifier (sender name + first ~80 chars of text, or media-type
// label when there's no text), and a × button that clears the
// target state.
//
// State lives on TicketDetail (plain useState, per PATTERNS.md
// "No React Context for ad-hoc state"). This component is purely
// visual + onCancel — it does not own the state, it does not call
// any API, it does not access ChatConfigContext.
//
// Mount slot: between the existing attachment preview / error
// banners (TicketDetail.tsx attachment-error + voice-error strips)
// and the composer wrapper. Each sibling strip carries its own ×
// button — this one is no different.

import { X } from 'lucide-react';
import type { Message } from '../../types/ticket';

export interface ReplyContextBarProps {
  /** The held message the technician is replying to. */
  target: Message;
  /** Optional display name for the target sender — when omitted,
   *  falls back to 'User' for incoming messages, or technicianName /
   *  'Support' for technician messages. The technician miniapp may
   *  pass undefined and rely on the fallback; the iframe-embedded
   *  chat surface may pass an explicit label. */
  senderLabel?: string;
  /** Tap × to clear the reply target. */
  onCancel: () => void;
}

const MEDIA_PLACEHOLDER_TEXTS = [
  '[photo]',
  '[document]',
  '[voice]',
  '[video]',
  '[audio]',
  '[sticker]',
];

/** First ~80 characters of the target's text, single-lined. Falls
 *  back to a media-type label when the target is a standalone media
 *  message. Returns empty string for the rare case of no text and
 *  no media type — caller should never hit this if MessageActionModal
 *  gated correctly. */
function previewText(msg: Message): string {
  if (
    msg.text &&
    msg.text.trim() &&
    !MEDIA_PLACEHOLDER_TEXTS.includes(msg.text)
  ) {
    const collapsed = msg.text.replace(/\s+/g, ' ').trim();
    return collapsed.length > 80 ? collapsed.slice(0, 80) + '…' : collapsed;
  }
  switch (msg.mediaType) {
    case 'photo':
      return 'Photo';
    case 'document':
      return msg.fileName || 'Document';
    case 'voice':
      return 'Voice message';
    case 'video':
      return 'Video';
    case 'audio':
      return msg.fileName || 'Audio';
    default:
      return '';
  }
}

function resolveSenderLabel(msg: Message, explicit?: string): string {
  if (explicit) return explicit;
  if (msg.from === 'user') return 'User';
  return msg.technicianName || 'Support';
}

export function ReplyContextBar({ target, senderLabel, onCancel }: ReplyContextBarProps) {
  const preview = previewText(target);
  const who = resolveSenderLabel(target, senderLabel);

  return (
    <div
      className="flex-none bg-white border-t px-4 py-2"
      role="region"
      aria-label="Replying to message"
    >
      <div className="flex items-center gap-3 max-w-4xl mx-auto">
        {/* iOS blue accent stripe — same token as MessageList ticket-divider
         *  buttons and MessageDeleteSheet. */}
        <div
          className="flex-shrink-0 w-[3px] self-stretch rounded-full"
          style={{ background: '#007AFF' }}
          aria-hidden
        />

        {/* Sender + preview. Single-line truncation on the preview line via
         *  `truncate`. If runtime testing shows the preview wraps too often,
         *  bump to a Tailwind line-clamp-2 utility — documented in
         *  33-05-SUMMARY.md. */}
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold leading-tight" style={{ color: '#007AFF' }}>
            Replying to {who}
          </div>
          <div className="text-[13px] text-gray-700 leading-tight truncate">
            {preview || '(no preview available)'}
          </div>
        </div>

        {/* × cancel — same icon library as the composer's other × buttons
         *  (lucide-react X icon, already imported in TicketDetail.tsx).
         *  aria-label so screen readers can act on it. */}
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel reply"
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 active:text-gray-900"
        >
          <X className="w-5 h-5" strokeWidth={2} aria-hidden />
        </button>
      </div>
    </div>
  );
}
